import { createPortal } from "react-dom"
import { ChevronUp } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { useBooking } from "@/context/BookingContext"
import { formatMoney, getGrandTotal } from "@/lib/booking"
import { usePortalContainer } from "@/widgets/core/PortalContainer"
import { PackageSummary } from "./PackageSummary"

interface MobileSummaryBarProps {
  /** Hides the bar (used when the inline summary is already visible on the page). */
  hidden?: boolean
}

export function MobileSummaryBar({ hidden = false }: MobileSummaryBarProps) {
  const { state } = useBooking()
  const container = usePortalContainer()
  const total = getGrandTotal(state)

  if (!state.selectedHotel && !(state.packageType === "flight-only" && state.selectedFlight)) return null

  const travelerLabel = `Total (${state.passengerCount} ${
    state.passengerCount === 1 ? "traveler" : "travelers"
  })`

  return (
    <Drawer>
      {/* Portaled out of the container subtree so it stays viewport-fixed (SPA)
          or widget-fixed instead of being trapped by container-type
          containment. The portaled root is the query container, so the
          "hide when wide" rule lives on the inner element. */}
      {createPortal(
        <div className="@container fixed inset-x-0 bottom-0 z-40">
          <div
            className={`transition-transform duration-300 ease-out @min-[64rem]:hidden ${
              hidden ? "pointer-events-none translate-y-full" : "translate-y-0"
            }`}
            aria-hidden={hidden}
          >
            <DrawerTrigger asChild>
              <Button
                variant="ghost"
                className="h-auto w-full justify-start gap-3 rounded-none border-t border-border bg-card px-4 py-3 hover:bg-card/80"
                tabIndex={hidden ? -1 : 0}
              >
                <div className="flex min-w-0 flex-1 flex-col text-left">
                  <span className="text-sm text-muted-foreground">
                    {travelerLabel}
                  </span>
                </div>

                <span className="text-lg font-bold text-primary">
                  {formatMoney(total, state.currency)}
                </span>

                <ChevronUp className="size-5 shrink-0 text-muted-foreground" />
              </Button>
            </DrawerTrigger>
          </div>
        </div>,
        container ?? document.body
      )}

      <DrawerContent className="@min-[64rem]:hidden">
        <DrawerHeader className="sr-only">
          <DrawerTitle>Your package summary</DrawerTitle>
          <DrawerDescription>
            Review your selected hotel, flight and extras.
          </DrawerDescription>
        </DrawerHeader>
        <div className="overflow-y-auto px-4 pb-6">
          <PackageSummary />
        </div>
      </DrawerContent>
    </Drawer>
  )
}
